import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './LocationEdit.module.css';
import { locationApi, Country, City } from '../../services/locationApi';

const LocationEdit: React.FC = () => {
  const { countryId } = useParams<{ countryId: string }>();
  const navigate = useNavigate();
  const isEdit = !!countryId;

  const [country, setCountry] = useState<Country>({
    Country_ID: '',
    name: '',
    abbrev: ''
  });
  const [cities, setCities] = useState<City[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [newCityName, setNewCityName] = useState('');
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [citiesLoading, setCitiesLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Fetch country and cities on component mount if editing
  useEffect(() => {
    if (isEdit && countryId) {
      fetchCountryData();
      fetchCities();
    }
  }, [isEdit, countryId]);

  // Filter cities when search term changes
  useEffect(() => {
    if (citySearchTerm) {
      const filtered = cities.filter(city =>
        city.name.toLowerCase().includes(citySearchTerm.toLowerCase())
      );
      setFilteredCities(filtered);
    } else {
      setFilteredCities(cities);
    }
  }, [citySearchTerm, cities]);

  const fetchCountryData = async () => {
    if (!countryId) {
      return;
    }
    try {
      setLoading(true);
  const data = await locationApi.getCountryById(countryId);
      setCountry(data);
    } catch (error: any) {
      console.error('Error fetching country:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to load country data. Please try again later.';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    if (!countryId) {
      return;
    }
    try {
      setCitiesLoading(true);
  const data = await locationApi.getAllCities(countryId);
      setCities(data);
      setFilteredCities(data);
    } catch (error: any) {
      console.error('Error fetching cities:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to load cities. Please try again later.';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setCitiesLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!country.name.trim()) {
      newErrors.name = 'Country name is required';
    }

    if (!country.abbrev.trim()) {
      newErrors.abbrev = 'Country abbreviation is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the form errors before submitting.', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      setLoading(true);
      
      const countryData = {
        name: country.name.trim(),
        abbrev: country.abbrev.trim()
      };

      let result;
      if (isEdit) {
        if (!countryId) {
          throw new Error('Country ID is missing.');
        }
        result = await locationApi.updateCountry(countryId, countryData);
        toast.success('Country updated successfully', {
          position: "top-right",
          autoClose: 3000
        });
      } else {
        result = await locationApi.createCountry(countryData);
        toast.success('Country created successfully', {
          position: "top-right",
          autoClose: 3000
        });
      }

      // Navigate to the edit page if it was a creation
      if (!isEdit && result.Country_ID) {
        navigate(`/admin/locations/${result.Country_ID}`);
      }
    } catch (error: any) {
      console.error('Error saving country:', error);
      const errorMessage = error?.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} country. Please try again.`;
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCity = async () => {
    if (!countryId) {
      toast.error('Missing country context.', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }
    if (!newCityName.trim()) {
      toast.error('Please enter a city name.', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      const cityData = {
        name: newCityName.trim(),
        Country_ID: countryId
      };

      await locationApi.createCity(cityData);
      setNewCityName('');
      fetchCities(); // Refresh cities list
      toast.success('City created successfully', {
        position: "top-right",
        autoClose: 3000
      });
    } catch (error: any) {
      console.error('Error creating city:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to create city. Please try again.';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
    }
  };

  const handleDeleteCity = (cityId: string, cityName: string) => {
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete the city "{cityName}"?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await locationApi.deleteCity(cityId);
                fetchCities();
                toast.success('City deleted successfully', {
                  position: "top-right",
                  autoClose: 3000
                });
              } catch (error: any) {
                console.error('Error deleting city:', error);
                const errorMessage = error?.response?.data?.detail || 'Failed to delete city. Please try again.';
                toast.error(errorMessage, {
                  position: "top-right",
                  autoClose: 3000
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
      }
    );
  };

  if (loading && isEdit) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading country data...</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/admin/locations"><i className="bi bi-geo-alt me-1"></i>Locations</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {isEdit ? country.name || 'Edit Country' : 'Create Country'}
            </li>
          </ol>
        </nav>

        <div className="mb-4">
          <h2 className="mb-1">{isEdit ? 'Edit Country' : 'Create Country'}</h2>
          <p className="text-muted mb-0">
            <i className="bi bi-geo-alt me-2"></i>
            {isEdit ? `Managing ${country.name || 'country'}` : 'Add a new country to the system'}
          </p>
        </div>

        <div className="row g-4">
          {/* Country Details Form */}
          <div className={`col-12 ${isEdit ? 'col-lg-7' : ''}`}>
            <div className={styles.formCard}>
              <div className={styles.formCardHeader}>
                <h5 className="mb-0">
                  <i className="bi bi-geo-alt me-2"></i>
                  Country Details
                </h5>
              </div>
              <div className={styles.formCardBody}>
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="inputName">
                        Country Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        id="inputName" 
                        type="text"
                        placeholder="Enter Country Name" 
                        value={country.name}
                        onChange={(e) => setCountry(prev => ({ ...prev, name: e.target.value }))}
                        required 
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="inputAbbrev">
                        Abbreviation <span className="text-danger">*</span>
                      </label>
                      <input 
                        className={`form-control ${errors.abbrev ? 'is-invalid' : ''}`}
                        id="inputAbbrev" 
                        type="text" 
                        placeholder="Enter Country Abbreviation"
                        value={country.abbrev}
                        onChange={(e) => setCountry(prev => ({ ...prev, abbrev: e.target.value }))}
                        maxLength={3}
                        style={{ textTransform: 'uppercase' }}
                        required 
                      />
                      {errors.abbrev && <div className="invalid-feedback">{errors.abbrev}</div>}
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-4 pt-3 border-top">
                    <button 
                      className="btn btn-primary px-4" 
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {isEdit ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          {isEdit ? 'Update Country' : 'Create Country'}
                        </>
                      )}
                    </button>
                    <Link to="/admin/locations" className="btn btn-outline-secondary px-4">
                      <i className="bi bi-arrow-left me-2"></i>
                      Back to Countries
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Cities Table - Only show if editing */}
          {isEdit && (
            <div className="col-12 col-lg-5">
              <div className={styles.tableContainer}>
                <div className={styles.tableHeader}>
                  <div className={styles.tableHeaderTop}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-buildings me-2 text-primary"></i>
                      <h5 className="mb-0">Cities</h5>
                    </div>
                    <span className="badge bg-primary rounded-pill">
                      {filteredCities.length} {filteredCities.length === 1 ? 'City' : 'Cities'}
                    </span>
                  </div>
                </div>

                {/* Add New City */}
                <div className="p-3 border-bottom bg-white">
                  <label className="form-label fw-semibold mb-2">Add New City</label>
                  <div className="input-group">
                    <input 
                      type="text" 
                      className="form-control"
                      placeholder="Enter city name" 
                      value={newCityName}
                      onChange={(e) => setNewCityName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCreateCity();
                        }
                      }}
                    />
                    <button 
                      className="btn btn-success" 
                      type="button"
                      onClick={handleCreateCity}
                      disabled={!newCityName.trim()}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Add
                    </button>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="p-3 border-bottom bg-white">
                  <div className="input-group">
                    <span className="input-group-text bg-white">
                      <i className="bi bi-search"></i>
                    </span>
                    <input 
                      type="text"
                      className="form-control"
                      placeholder="Search cities..."
                      value={citySearchTerm}
                      onChange={(e) => setCitySearchTerm(e.target.value)}
                    />
                    {citySearchTerm && (
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => setCitySearchTerm('')}
                        title="Clear search"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    )}
                  </div>
                </div>

                {/* Desktop Table View */}
                <div className={styles.desktopTable}>
                  <div className={styles.cityListContainer}>
                    {citiesLoading ? (
                      <div className="text-center p-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2 small text-muted">Loading cities...</div>
                      </div>
                    ) : filteredCities.length === 0 ? (
                      <div className={styles.emptyState}>
                        {citySearchTerm ? (
                          <>
                            <i className="bi bi-search display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No cities found</h5>
                            <p className="text-muted">Try adjusting your search.</p>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-buildings display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No cities yet</h5>
                            <p className="text-muted">Add your first city above.</p>
                          </>
                        )}
                      </div>
                    ) : (
                      <table className={`table ${styles.cityTable}`}>
                        <thead>
                          <tr>
                            <th>
                              <i className="bi bi-building me-1"></i>
                              City Name
                            </th>
                            <th>
                              <i className="bi bi-tools me-1"></i>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCities.map((city, index) => (
                            <tr key={city.City_ID} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                              <td className={styles.cityTableCell}>
                                <strong>{city.name}</strong>
                              </td>
                              <td className={`${styles.cityTableCell} text-end`}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteCity(city.City_ID, city.name)}
                                  title={`Delete ${city.name}`}
                                >
                                  <i className="bi bi-trash-fill"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className={styles.mobileCards}>
                  <div className={styles.cityListContainer}>
                    {citiesLoading ? (
                      <div className="text-center p-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2 small text-muted">Loading cities...</div>
                      </div>
                    ) : filteredCities.length === 0 ? (
                      <div className={styles.emptyState}>
                        {citySearchTerm ? (
                          <>
                            <i className="bi bi-search display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No cities found</h5>
                            <p className="text-muted">Try adjusting your search.</p>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-buildings display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No cities yet</h5>
                            <p className="text-muted">Add your first city above.</p>
                          </>
                        )}
                      </div>
                    ) : (
                      filteredCities.map((city) => (
                        <div key={city.City_ID} className={styles.cityCard}>
                          <div className={styles.cityCardHeader}>
                            <h6 className="mb-0 fw-bold">{city.name}</h6>
                          </div>
                          <div className={styles.cityCardActions}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger flex-fill"
                              onClick={() => handleDeleteCity(city.City_ID, city.name)}
                              title={`Delete ${city.name}`}
                            >
                              <i className="bi bi-trash-fill me-1"></i>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationEdit;
